import Add16 from '@carbon/icons-react/es/add/16';
import Partnership20 from '@carbon/icons-react/es/partnership/20';
import { SecureRoute } from '@okta/okta-react';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Switch, useHistory, useRouteMatch } from 'react-router';
import styled from 'styled-components';
import { PartnerCompany } from 'api/partner/types';
import { Button } from 'components/atoms/button';
import { SearchInput } from 'components/atoms/input';
import { PageLoading } from 'components/atoms/loading';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { PartnerListGrid } from 'components/organisms/module-settings/partner';
import { PartnerListEmptyState } from 'components/organisms/module-settings/partner/list/empty-state';
import { PartnerRolesSelectionModal } from 'components/organisms/module-settings/partner/list/partner-roles-selection';
import { Argon as ArgonTemplate } from 'components/templates';
import { Content } from 'components/templates/argon/components';
import { useTranslation } from 'hooks/use-translation';
import {
  useGetAllPartnerCompanies,
  useGetAllPartnerPermissions,
} from 'queries/partner';
import { useVendorRolesQuery } from 'queries/roles';
import { NewPartner } from './new-partner';

const FilterBar = styled.div`
  gap: var(--spacing-05);
  display: flex;
  align-items: center;
  flex: 1;
`;
const ContentWrapper = styled.div`
  padding: 0 var(--spacing-05);
  height: 100%;
`;

const Argon = styled(ArgonTemplate)`
  ${Content} {
    border: none;
  }
`;

export const PartnerList: FC = () => {
  const { path } = useRouteMatch();
  const { push } = useHistory();
  const { data: partners, isLoading: isPartnerLoading } =
    useGetAllPartnerCompanies();
  const { data: partnerPermissions, isLoading: isPartnerPermissionsLoading } =
    useGetAllPartnerPermissions();
  const [searchText, setSearchText] = useState('');
  const filterFunction = useCallback(
    (partner: PartnerCompany) => {
      return partner.companyName
        .toLowerCase()
        .includes(searchText.trim().toLowerCase());
    },
    [searchText],
  );
  const { data: roles, isLoading: isRoleLoading } = useVendorRolesQuery();
  const isPartnerRoleAvailable = useMemo(
    () => (roles ?? []).some(({ usedByPartners }) => usedByPartners === true),
    [roles],
  );
  const [showRoleSelectionModal, setRoleSelectedModalVisibility] =
    useState(false);
  const openNewPartnerDrawer = useCallback(() => {
    push(`${path}/new`);
  }, [path, push]);
  const beforeNewPartner = useCallback(() => {
    if (!isPartnerRoleAvailable) {
      setRoleSelectedModalVisibility(true);
    } else {
      openNewPartnerDrawer();
    }
  }, [isPartnerRoleAvailable, openNewPartnerDrawer]);
  const closeRoleSelectionModal = useCallback(() => {
    setRoleSelectedModalVisibility(false);
  }, []);
  const afterRoleSelection = useCallback(() => {
    setRoleSelectedModalVisibility(false);
    openNewPartnerDrawer();
  }, [openNewPartnerDrawer]);
  const pageContentLoading =
    isPartnerLoading || isPartnerPermissionsLoading || isRoleLoading;
  const searchInputPlaceholder = useTranslation('partners.search.placeholder');

  return (
    <Argon
      title={
        <HeaderWithIconTitle
          icon={<Partnership20 />}
          title={useTranslation('partners')}
        />
      }
      documentTitle={useTranslation('partners')}
      actions={
        pageContentLoading
          ? {}
          : {
              primary: <></>,
              secondary: (
                <FilterBar>
                  <SearchInput
                    direction="right"
                    searchButtonProps={{ type: 'text' }}
                    size="small"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                    }}
                    placeholder={searchInputPlaceholder}
                  />
                  <Button
                    type="primary"
                    iconPlacement="right"
                    size="small"
                    icon={<Add16 />}
                    onClick={beforeNewPartner}
                  >
                    <FormattedMessage id="partners.add" />
                  </Button>
                </FilterBar>
              ),
            }
      }
      content={
        <>
          {pageContentLoading ? (
            <PageLoading />
          ) : (
            <ContentWrapper>
              {partners == null || partners.length === 0 ? (
                <PartnerListEmptyState onClick={beforeNewPartner} />
              ) : (
                <PartnerListGrid
                  partners={partners ?? []}
                  partnerPermissions={partnerPermissions ?? []}
                  filterFunction={filterFunction}
                />
              )}
              <PartnerRolesSelectionModal
                visible={showRoleSelectionModal}
                onClose={closeRoleSelectionModal}
                onRoleMigrationDone={afterRoleSelection}
              />
              <Switch>
                <SecureRoute
                  path={`${path}/new`}
                  render={() => <NewPartner />}
                />
              </Switch>
            </ContentWrapper>
          )}
        </>
      }
    />
  );
};
